import { useEmblaCarousel } from 'embla-carousel/react';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import Thumb from './Thumb';
import { Image } from '@utmb/design-system/image/Image';

import styles from './caroussel.module.scss';
import { Icon } from '../../design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import SlicePhotoGalleryPhotoView from '@utmb/types/view/SlicePhotoGalleryPhotoView';
import { Font } from '@utmb/design-system/text/font';

interface CarousselProps {
    imgs: SlicePhotoGalleryPhotoView[];
    autoplay: boolean;
}

const Caroussel = (props: CarousselProps): ReactElement => {
    const { imgs, autoplay } = props;

    const [selectedIndex, setSelectedIndex] = useState(0);
    const divRef = useRef<HTMLDivElement>(null);
    const [mainViewportRef, embla] = useEmblaCarousel({ skipSnaps: false, loop: true });
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
    const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        selectedClass: '',
        dragFree: true,
    });

    const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
    const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

    useEffect(() => {
        if (autoplay && embla) {
            if (embla.canScrollNext()) {
                setInterval(() => {
                    embla.scrollNext();
                }, 5000);
            } else {
                setInterval(() => {
                    embla.scrollTo(0);
                }, 5000);
            }
        }
    }, [autoplay, embla]);

    const onThumbClick = useCallback(
        (index) => {
            if (!embla || !emblaThumbs) return;
            if (emblaThumbs.clickAllowed()) embla.scrollTo(index);
        },
        [embla, emblaThumbs]
    );

    const onSelect = useCallback(() => {
        if (!embla || !emblaThumbs) return;
        setSelectedIndex(embla.selectedScrollSnap());
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
        emblaThumbs.scrollTo(embla.selectedScrollSnap());
    }, [embla, emblaThumbs, setSelectedIndex]);

    useEffect(() => {
        if (!embla) return;
        onSelect();
        embla.on('select', onSelect);
    }, [embla, onSelect]);

    return (
        <div style={{ textAlign: 'center' }} className={styles.embla}>
            <div ref={divRef} style={{ height: 'calc(100vw * (16 / 9)) px', position: 'relative', minHeight: 'calc(100vw * (16 / 9)) px' }}>
                <div className={styles.embla__viewport} ref={mainViewportRef}>
                    <div className={styles.embla__container}>
                        {imgs.map((img, index) => (
                            <div className={styles.embla__slide} key={index}>
                                <div className={styles.embla__slide__inner}>
                                    <Image image={img.picture} alt={img.picture.alt} className={styles.embla__slide__img} objectFit="cover" />

                                    {img.title && (
                                        <div className={styles.slideCaption}>
                                            <Font.FuturaBold desktop="16" mobile="14">
                                                {img.title}
                                            </Font.FuturaBold>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <button className={[styles.embla__button, styles.embla__button_next].join(' ')} type="button" onClick={scrollPrev} disabled={!prevBtnEnabled}>
                    <Icon variant={IconsEnum.CHEVRON_LEFT} clss={styles.icon} />
                </button>
                <button className={[styles.embla__button, styles.embla__button_prev].join(' ')} type="button" onClick={scrollNext} disabled={!nextBtnEnabled}>
                    <Icon variant={IconsEnum.CHEVRON_RIGHT} clss={styles.icon} />
                </button>
            </div>

            <div className={styles.embla_thumb}>
                <div className={[styles.embla__viewport, styles.embla__limit_height].join(' ')} ref={thumbViewportRef}>
                    <div className={[styles.embla__container, styles.embla__container_thumb].join(' ')}>
                        {imgs.map((img, index) => (
                            <Thumb img={img.picture} onClick={() => onThumbClick(index)} selected={index === selectedIndex} key={index} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Caroussel;
